import { Fragment, useRef, useState } from 'react';

// Stored form of a mention: @[Full Name](userId). The backend validates every token
// against project members and rewrites the name, so this format is the contract.
const MENTION_TOKEN = /@\[([^[\]\r\n]{1,255})\]\((\d{1,12})\)/g;
const MAX_SUGGESTIONS = 8;
const QUERY_BEFORE_CARET = /(^|\s)@([\p{L}\p{N}._'-]{0,40}(?: [\p{L}\p{N}._'-]{0,40}){0,2})$/u;

// Must stay in sync with mention_token_name() in backend/app/tasks/mentions.py.
export const mentionTokenName = name => (name || '').replace(/[[\]\r\n]/g, '').trim() || 'Użytkownik';

export const normalizeForSearch = text => (text || '')
  .replace(/ł/g, 'l').replace(/Ł/g, 'L')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLocaleLowerCase('pl');

const escapeRegExp = text => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Turns "@Jan Kowalski" picked from the list into a token. A mention the user edited
// afterwards no longer matches and simply stays plain text (nobody is notified).
export function serializeMentions(text, mentions) {
  const byName = new Map();
  mentions.forEach(person => {
    const name = mentionTokenName(person.full_name);
    if (!byName.has(name)) byName.set(name, person.id);
  });
  if (!byName.size) return text;
  const names = [...byName.keys()].sort((a, b) => b.length - a.length).map(escapeRegExp);
  const pattern = new RegExp(`(^|[^\\p{L}\\p{N}_@])@(${names.join('|')})(?![\\p{L}\\p{N}_])`, 'gu');
  return text.replace(pattern, (_, prefix, name) => `${prefix}@[${name}](${byName.get(name)})`);
}

export function parseMentions(content) {
  const segments = [];
  let last = 0;
  for (const match of (content || '').matchAll(MENTION_TOKEN)) {
    if (match.index > last) segments.push({ type: 'text', text: content.slice(last, match.index) });
    segments.push({ type: 'mention', name: match[1], id: Number(match[2]) });
    last = match.index + match[0].length;
  }
  if (last < (content || '').length) segments.push({ type: 'text', text: content.slice(last) });
  return segments;
}

export function findMentionQuery(text, caret) {
  const match = text.slice(0, caret).match(QUERY_BEFORE_CARET);
  if (!match) return null;
  return { start: match.index + match[1].length, end: caret, text: match[2] };
}

export function filterMentionCandidates(people, query, excludedId) {
  const normalizedQuery = normalizeForSearch(query);
  return people.filter(person => {
    if (person.id === excludedId) return false;
    const name = normalizeForSearch(person.full_name);
    return !normalizedQuery || name.startsWith(normalizedQuery)
      || name.split(/\s+/).some(part => part.startsWith(normalizedQuery));
  }).slice(0, MAX_SUGGESTIONS);
}

export function CommentContent({ content, currentUserId }) {
  return parseMentions(content).map((segment, index) => segment.type === 'mention'
    ? <span key={index} className={`tp-mention${segment.id === currentUserId ? ' tp-mention-me' : ''}`}
      title={segment.id === currentUserId ? 'Oznaczono Ciebie' : undefined}>@{segment.name}</span>
    : <Fragment key={index}>{segment.text}</Fragment>);
}

export function MentionTextarea({
  id, value, onChange, mentions, onMentionsChange, people, currentUserId, renderAvatar, disabled, ...textareaProps
}) {
  const textareaRef = useRef(null);
  const [query, setQuery] = useState(null);
  const [active, setActive] = useState(0);
  const listId = `${id}-mentions`;
  const suggestions = query && !disabled ? filterMentionCandidates(people, query.text, currentUserId) : [];
  const open = suggestions.length > 0;
  const activeIndex = Math.min(active, suggestions.length - 1);

  const refresh = (text, caret) => {
    const found = findMentionQuery(text, caret ?? text.length);
    setQuery(found);
    if (!found || found.start !== query?.start) setActive(0);
  };

  const pick = person => {
    const name = mentionTokenName(person.full_name);
    const before = value.slice(0, query.start);
    const after = value.slice(query.end).replace(/^ /, '');
    const inserted = `@${name} `;
    onChange(before + inserted + after);
    if (!mentions.some(item => item.id === person.id)) onMentionsChange([...mentions, { id: person.id, full_name: name }]);
    setQuery(null);
    const caret = before.length + inserted.length;
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(caret, caret);
    });
  };

  const onKeyDown = event => {
    if (!open) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((activeIndex + step + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      pick(suggestions[activeIndex]);
    } else if (event.key === 'Escape') {
      // Close only the suggestions, not the task drawer listening on document.
      event.preventDefault();
      event.stopPropagation();
      setQuery(null);
    }
  };

  return <div className="tp-mention-field">
    <textarea {...textareaProps} id={id} ref={textareaRef} value={value} disabled={disabled}
      aria-autocomplete="list" aria-controls={open ? listId : undefined}
      aria-activedescendant={open ? `${listId}-${suggestions[activeIndex].id}` : undefined}
      onChange={event => { onChange(event.target.value); refresh(event.target.value, event.target.selectionStart); }}
      onKeyDown={onKeyDown}
      onClick={event => refresh(event.target.value, event.target.selectionStart)}
      onKeyUp={event => {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) refresh(event.target.value, event.target.selectionStart);
      }}
      onBlur={() => setQuery(null)} />
    {open && <ul id={listId} role="listbox" className="tp-mention-list" aria-label="Oznacz osobę z projektu">
      {suggestions.map((person, index) => <li key={person.id} id={`${listId}-${person.id}`} role="option"
        aria-selected={index === activeIndex} className={index === activeIndex ? 'tp-mention-option-active' : ''}
        onMouseDown={event => { event.preventDefault(); pick(person); }} onMouseEnter={() => setActive(index)}>
        {renderAvatar?.(person)}<span>{person.full_name}</span>
      </li>)}
    </ul>}
    <span className="tp-visually-hidden" role="status" aria-live="polite">
      {open ? `Podpowiedzi osób: ${suggestions.length}. Wybierz strzałkami i zatwierdź Enter.` : ''}
    </span>
  </div>;
}
